'use client';

import type { GA4Item } from '@lib/analytics/types';
import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import { useProductListing } from '@lib/hooks/use-product-listing';
import {
  buildPlpLinkParams,
  DEFAULT_PLP_SORT,
  parsePageParam,
  parsePriceParam,
  parseSortParam,
} from '@lib/util/pagination';
import type { SortOptions } from '@lib/util/sort-products';
import { TrackItemList } from '@modules/analytics/components/trackers';
import { Spinner } from '@modules/common/icons';
import PlpLiveStatus from '@modules/store/components/plp-live-status';
import PlpPagination from '@modules/store/components/plp-pagination';
import ProductGrid from '@modules/store/components/product-grid';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';
import { ProductGridEmptyState } from '@modules/store/templates/product-grid-empty-state';
import { ProductGridErrorState } from '@modules/store/templates/product-grid-error-state';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductCardVM } from 'types/view-models';

const ProductListingToolbar = dynamic(
  () => import('@modules/store/components/product-listing-toolbar'),
);

const PRODUCT_GRID_SCROLL_TARGET = 'products-list';

type ProductListingProps = {
  countryCode: string;
  categoryId?: string;
  collectionId?: string;
  searchQuery?: string;
  listId?: string;
  listName?: string;
  defaultProducts: ProductCardVM[];
  defaultGa4Items: GA4Item[];
  defaultCount: number;
};

type ListingParams = {
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
  page: number;
};

const DEFAULT_LISTING_PARAMS: ListingParams = { sortBy: 'popular', page: 1 };

// Deliberately does NOT use `useSearchParams()` — that hook forces Next.js to
// bail this component out of static/ISR rendering and paint it only after
// client JS hydrates (behind a Suspense fallback), so with JS disabled the
// product grid would never appear at all. Sort/price/page state instead
// starts at the default (rendering `defaultProducts` — identical to what the
// server fetched — on every render pass: server, first client paint, and
// post-hydration) and is corrected client-side, after mount, by reading the
// URL directly. That keeps the default view (the common case) visible
// without JS and free of any hydration swap/layout shift.
function readParamsFromLocation(): ListingParams {
  const params = new URLSearchParams(window.location.search);
  return {
    sortBy: parseSortParam(params.get('sortBy')),
    minPrice: parsePriceParam(params.get('minPrice')),
    maxPrice: parsePriceParam(params.get('maxPrice')),
    page: parsePageParam(params.get('page')),
  };
}

export default function ProductListing({
  countryCode,
  categoryId,
  collectionId,
  searchQuery,
  listId,
  listName,
  defaultProducts,
  defaultGa4Items,
  defaultCount,
}: ProductListingProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [{ sortBy, minPrice, maxPrice, page }, setListingParams] =
    useState<ListingParams>(DEFAULT_LISTING_PARAMS);

  // Mirrors the latest listing params outside of React state so
  // `updateListingParams`/`handlePageChange` never read a stale value if
  // called again before the state update above has re-rendered this
  // component (e.g. two distinct filter controls triggered in the same tick).
  const listingParamsRef = useRef<ListingParams>(DEFAULT_LISTING_PARAMS);

  // Corrects state from the real URL once mounted (deep links to a non-default
  // sort/filter/page) and keeps it in sync with browser back/forward navigation.
  useEffect(() => {
    const params = readParamsFromLocation();
    listingParamsRef.current = params;
    setListingParams(params);
    const onPopState = () => {
      const next = readParamsFromLocation();
      listingParamsRef.current = next;
      setListingParams(next);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Sort/price changes always jump back to page 1 (A/C: "If a customer
  // applies filter/sort on page 2/3 etc, then the customer will be taken
  // back to page 1").
  const updateListingParams = useCallback(
    (
      updates: Partial<Pick<ListingParams, 'sortBy' | 'minPrice' | 'maxPrice'>>,
    ) => {
      const next = { ...listingParamsRef.current, ...updates, page: 1 };
      listingParamsRef.current = next;
      const urlParams = new URLSearchParams(window.location.search);
      // The default sort is removed, not written — keeps the URL in its
      // clean, canonical no-param form (mirrors `buildPlpLinkParams`).
      if (next.sortBy === DEFAULT_PLP_SORT) {
        urlParams.delete('sortBy');
      } else {
        urlParams.set('sortBy', next.sortBy);
      }
      if (next.minPrice != null) {
        urlParams.set('minPrice', String(next.minPrice));
      } else {
        urlParams.delete('minPrice');
      }
      if (next.maxPrice != null) {
        urlParams.set('maxPrice', String(next.maxPrice));
      } else {
        urlParams.delete('maxPrice');
      }
      urlParams.delete('page');
      setListingParams(next);
      const query = urlParams.toString();
      router.push(query ? `${pathname}?${query}` : pathname);
    },
    [pathname, router],
  );

  const handleSortChange = useCallback(
    (value: string) => updateListingParams({ sortBy: value as SortOptions }),
    [updateListingParams],
  );

  const handlePriceApply = useCallback(
    ({ min, max }: { min: number | null; max: number | null }) =>
      updateListingParams({
        minPrice: min ?? undefined,
        maxPrice: max ?? undefined,
      }),
    [updateListingParams],
  );

  const { navigate: navigatePage, isPending: isPagePending } =
    useNavigationLoading();

  const { products, ga4Items, count, isOverriding, isError, retry } =
    useProductListing({
      sortBy,
      minPrice,
      maxPrice,
      page,
      countryCode,
      categoryId,
      collectionId,
      searchQuery,
      listId,
      listName,
      defaultProducts,
      defaultGa4Items,
      defaultCount,
    });

  // Preserves sortBy/minPrice/maxPrice on pagination links without touching
  // them — built from state, not `window.location`, so it stays correct
  // across re-renders triggered by state alone (no URL round-trip needed).
  const paginationParams = buildPlpLinkParams({ sortBy, minPrice, maxPrice });

  return (
    <div id={PRODUCT_GRID_SCROLL_TARGET}>
      <PlpLiveStatus
        isLoading={isOverriding || isPagePending}
        hasError={isError}
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={count}
      />
      <TrackItemList items={ga4Items} listId={listId} listName={listName} />
      <ProductListingToolbar
        sortBy={sortBy}
        page={page}
        productCount={count}
        minPrice={minPrice}
        maxPrice={maxPrice}
        isLoading={isOverriding}
        onSortChange={handleSortChange}
        onPriceApply={handlePriceApply}
      />
      <ProductListingBody
        isOverriding={isOverriding || isPagePending}
        isError={isError}
        onRetry={retry}
        products={products}
        page={page}
      />
      <PlpPagination
        pathname={pathname}
        currentParams={paginationParams}
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={count}
        navigate={navigatePage}
      />
    </div>
  );
}

type ProductListingBodyProps = {
  isOverriding: boolean;
  isError: boolean;
  onRetry: () => void;
  products: ProductCardVM[];
  page: number;
};

// Shows the spinner while EITHER the client-side sort/price refetch
// (`isOverriding`, `use-product-listing.ts`) OR a page-change navigation to
// the dynamic route (`isPagePending`, `useNavigationLoading`) is in flight — one
// visual for both param-change paths, matching the pre-pagination behaviour
// that only had the former.
function ProductListingBody({
  isOverriding,
  isError,
  onRetry,
  products,
  page,
}: ProductListingBodyProps) {
  if (isOverriding) {
    // Announcements come from the persistent PlpLiveStatus region above —
    // the spinner itself is purely visual.
    return (
      <div className="flex justify-center py-40" aria-hidden="true">
        <Spinner
          size={48}
          className="text-fg-interactive-strong animate-spin motion-reduce:animate-none"
        />
      </div>
    );
  }

  if (isError) {
    return <ProductGridErrorState onRetry={onRetry} />;
  }

  if (products.length === 0) {
    return <ProductGridEmptyState />;
  }

  return (
    <div className="animate-fade-in">
      <ProductGrid products={products} page={page} />
    </div>
  );
}
